import React, { FC, useEffect, useRef, useState } from 'react';
import Label from '../label/label';
import { useFormikContext, Field } from 'formik';
import { addCustomFieldMetaData } from '../../utilities/formFields';
import { generateFieldId } from '../../utilities/GenerateFieldId';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import '../user-card/user-card.css';
import useFocusOnRouteChange from '../../hooks/useFocusOnRouteChange';

interface SettingTextFieldProps {
    accessor: string;
    displayName: string | undefined;
    required?: boolean;
    isCard?: boolean;
    validation?: (value) => string;
    maxLength?: number;
    onKeyDown?: (value) => void;
    multiLine?: boolean;
    characterCount?: boolean;
    isPassword?: boolean;
    detailTypeId?: number;
    numberUnit?: string;
    isDecimal?: boolean;
    tooltip?: string;
    warning?: string;
    handleWarning?: (value: string) => string
    autoFocus?: boolean;
    hidePadding?: boolean;
}

export const SettingTextField: FC<SettingTextFieldProps> = ({ isDecimal = false, accessor, displayName, required = false, isCard = false, validation, maxLength, onKeyDown, multiLine = false, characterCount = false, isPassword = false, detailTypeId = undefined, numberUnit = undefined, tooltip = undefined, handleWarning = undefined, autoFocus= false, hidePadding = false}) => {
    const { handleChange, values, setFieldValue, errors } = useFormikContext<any>();
    const [fieldLength, setFieldLength] = useState<number>(0);
    const { translate } = useLocale();
    const [warning, setWarning] = useState<string>(''); //Should not block saving. Here for those field we can't truly validate e.g. Phone Number

    useEffect(() => {
        addCustomFieldMetaData(detailTypeId, values, accessor, setFieldValue, false, displayName);
    }, [])

    const onChange = (e) => {
        const currentDisplayName = values.name?.trim();
        const currentFirstName = values.firstName?.trim();
        const currentLastName = values.lastName?.trim();
        const firstName = 'firstName';
        const lastName = 'lastName';
        const name = 'name';

        if (values) {
            if (
                (e.target.name === firstName && currentDisplayName === currentFirstName) ||
                (e.target.name === lastName && currentDisplayName === (currentFirstName + ' ' + currentLastName).trim())
            ) {
                const newDisplayName = e.target.name === firstName ? e.target.value : currentFirstName + ' ' + e.target.value;
                setFieldValue(name, newDisplayName);
            } else if (e.target.name === firstName && currentDisplayName === (currentFirstName + ' ' + currentLastName).trim()) {
                const newDisplayName = e.target.value + ' ' + currentLastName;
                setFieldValue(name, newDisplayName);
            }
        }

        if (handleWarning) {
            setWarning(handleWarning(e.target.value));
        }

        handleChange(e);
    };

    const inputRef = useRef<HTMLInputElement>(null);
    useFocusOnRouteChange(inputRef);
    
    return <div className={`${isCard ? '' : `flex flex-col flex-1 ${hidePadding ? "" : "pr-6"}`} mb-2`}>
        <div className="flex flex-row justify-between" id={generateFieldId(accessor, 'TextFieldLabel')}>
            <Label htmlFor={accessor}
                className="text-xs text-gray-600 mt-1" required={required}>{displayName}:{numberUnit ? <span className="italic text-gray-500">&nbsp;({numberUnit})</span> : ''}</Label>
            {(() => {
                const error = accessor.split('.').reduce((previous, current) => { return previous?.[current] }
                    , errors as any);
                if (error) {
                    return <span data-cy={`${accessor}ErrorLabel`} className='text-red-600 text-xs'>{error}</span>
                } else if (warning) { 
                    return <span data-cy={`${accessor}WarningLabel`} className='text-orange-600 text-xs'>{warning}</span>
                }
                else {
                    return null;
                }
            })()}
        </div>
        <Field
            id={generateFieldId(accessor, 'TextField')}
            name={accessor}
            type={isPassword ? 'password' : (!!numberUnit) || isDecimal ? 'number' : 'text'}
            component={multiLine ? 'textarea' : null}
            className={`p-1 text-sm w-full bg-gray-input overflow-hidden ${isCard ? 'mb-2' : ''} ${multiLine ? 'w-full h-32' : ''}`}
            title={tooltip}
            innerRef={autoFocus ?inputRef:undefined}
            autoFocus={autoFocus}
            onChange={onChange}
            validate={validation}
            maxLength={maxLength}
            onKeyDown={onKeyDown}
            onKeyUp={(event) => characterCount ? setFieldLength(event.target.value.length) : null}
            autoComplete={isPassword ? "new-password" : "off"} />
        {characterCount ? <span>{translate.byKeyFormatted(TranslationKeys.character, [fieldLength])}</span> : null}
    </div>;
};
